import { MatMenuTrigger } from '@angular/material/menu';
import { VisitorPlaceService } from 'src/app/modules/event-calendar/services/visitor.place.service';
import { VisitorPlace } from './../../../shared/models/visitor.place';
import { inject, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Observable, delay, finalize, tap, timer } from 'rxjs';
import { BaseComponent } from 'src/app/common/components/base.component';
import { ErrorAlertService } from 'src/app/common/services/error.alert/error.alert.service';
import { SeoService } from 'src/app/common/services/seo.service/seo.service';
import { IPlaceDetailsModalData } from './place.details.modal.provider';
import { ConfigService } from 'src/app/common/services/config/config.service';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import { clone, isEmptyArray, isNullOrUndefined } from 'src/app/common/utils/object.extensions';
import { VisitorEvent } from 'src/app/modules/shared/models/visitor.event';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { CalendarQueryCommand } from 'src/app/modules/shared/models/calendar.action.command';
import {
  IAppState,
  IFrontendSettings,
} from 'src/app/modules/shared/ngrx.stores/frontend.settings/states';
import { Store, select } from '@ngrx/store';
import { frontendSettingsSelector } from 'src/app/modules/shared/ngrx.stores/frontend.settings/selectors';
import { IMapMarkerWithCustomData } from 'src/app/modules/shared/models/place.marker';
import { VisitorService } from 'src/app/modules/shared/services/visitor.service';
import { GoogleAnalyticsService } from 'src/app/common/services/google.analytics/google.analytics.service';

export class PlaceDetailsBaseComponent extends BaseComponent {
  isOpened = false;
  isLoading = false;
  place: VisitorPlace;
  hasServes = false;
  imgIndex = 0;
  currentImg: string;
  hasNextImg = true;
  hasPrevImg = false;
  hasMultiImages = true;
  isLoadingImg = false;
  isLoadingNearby = false;
  mapMarkers: IMapMarkerWithCustomData<VisitorPlace | VisitorEvent>[] = [];
  nearByPlaces$: Observable<VisitorPlace[] | null>;
  nearByEvents: VisitorEvent[] = [];
  filteredNearByEvents: VisitorEvent[] | null = null;
  isShowingNearbyEventsOnMap = false;
  isShowingNearbyPlacesOnMap = false;
  settings: IFrontendSettings;
  youtubeEmbedUrl: SafeResourceUrl | null = null;

  visitorService = inject(VisitorService);
  gaService = inject(GoogleAnalyticsService);

  constructor(
    public activatedRoute: ActivatedRoute,
    public seoService: SeoService,
    public errorAlert: ErrorAlertService,
    public sanitizer: DomSanitizer,
    public placeService: VisitorPlaceService,
    public modalService: ModalService,
    public dialogRef: MatDialogRef<PlaceDetailsBaseComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IPlaceDetailsModalData,
    public _store: Store<IAppState>
  ) {
    super({ activatedRoute });

    this._store.pipe(select(frontendSettingsSelector)).subscribe((settings) => {
      settings && (this.settings = settings);
    });

    setTimeout(() => {
      this.loadPlace();

      this.dialogRef
        ?.afterOpened()
        .pipe(delay(60))
        .subscribe(() => {
          this.isOpened = true;
        });
    }, 100);
  }

  public get placeNameAddress() {
    if (this.place?.address?.addressComponents) {
      if (!isEmptyArray(this.place.address.addressComponents)) {
        const getShortName = (type: string) => {
          const comp = this.place.address.addressComponents.find((x) => x.types?.includes(type));
          return comp?.short_name ?? comp?.shortText ?? '';
        };
        return `${this.place.name}, ${getShortName('locality')}, ${getShortName('administrative_area_level_1')}`;
      }
    }

    return this.place.name + ', ' + this.place.address.address;
  }

  public get videoTitle() {
    let title = '';
    if (this.place.youtubeLink) {
      title += 'Youtube Video';
    }
    if (this.place._360VideoKey) {
      title += title ? ' & 360° Video' : '360° Video';
    }
    return title;
  }

  public loadPlace() {
    this.isLoading = true;
    if (this.data.placeDetailsId) {
      this.placeService
        .getPlaces(this.hubName, {
          id: this.data.placeDetailsId,
        } as CalendarQueryCommand)
        .subscribe((places) => {
          if (isEmptyArray(places)) {
            this.errorAlert.showErrorAlert(
              "Uh-oh! Looks like we haven't found this place! Please view other places in the list!"
            );
            this.dialogRef.close();
            return;
          }

          this.place = places.first();
          this.ensurePlaceDefaults(this.place);
          if (!this.place.isManuallyCreated) {
            this.getPlaceDetails();
          } else {
            this.processPlace();
            this.getPlaceImages();
            this.isLoading = false;
          }
        });
    } else {
      this.place = Object.assign({}, this.data.currentPlace);
      this.ensurePlaceDefaults(this.place);
      if (!this.place.isManuallyCreated) {
        this.getPlaceDetails();
      } else {
        this.processPlace();
        this.getPlaceImages();
        this.isLoading = false;
      }
    }
  }

  public getDirection(place: VisitorPlace) {
    let address = place.address?.address || place.address;
    let url = ConfigService.config.google.googleMapGetDirection.replace(
      '${address}',
      encodeURIComponent(address.toString())
    );
    window.open(url, '_blank');
  }

  public get hasWebsite() {
    return this.place?.website || this.place?.hostLink;
  }

  public visitWebsite() {
    window.open(this.place.website || this.place.hostLink, '_blank');
    this.gaService.trackViewWebsite(this.place.name);
    if (this.hubName === 'SidneyCheyenneCoNE') {
      this.gaService.trackFBPixelPageView(this.place.name, location.href, 'Lead');
    }
  }

  public get hasFacebookLink() {
    return this.place?.facebookLink;
  }

  public visitFacebookPage() {
    window.open(this.place.facebookLink, '_blank');
  }

  public showLink() {
    this.copyToClip(window.location.href);
    alert(`The following text will be copied into your clipboard:\n\n ${window.location.href}`);
  }

  // Youtube video link looks like:
  // https://www.youtube.com/watch?v=puKsjFRwIyw
  // https://www.youtube.com/watch?v=puKsjFRwIyw&t=27s
  public getYoutubeLink() {
    return this.placeService.getYoutubeLink(this.place);
  }

  public get360VideoLink() {
    return this.placeService.get360VideoLink(this.place);
  }

  public getYoutubeThumbnail(): string {
    if (this.place?.youtubeLink?.includes('watch?v=')) {
      const code = this.place.youtubeLink.split('watch?v=')[1].split('&')[0];
      return `https://img.youtube.com/vi/${code}/hqdefault.jpg`;
    }
    return '';
  }

  public loadNearbyEvents(range: number) {
    this.isLoadingNearby = true;
    this.placeService.getNearByEvents(this.place, range).subscribe((events) => {
      this.nearByEvents = events;
      this.isLoadingNearby = false;
    });
  }

  public toggleShowNearbyEventsOnMap(status: MatCheckboxChange) {
    if (status.checked) {
      this.isShowingNearbyPlacesOnMap = false;

      const events = this.nearByEvents.filter(
        (x) => !isNullOrUndefined(x.address.lat) && !isNullOrUndefined(x.address.lng)
      );
      const markers = events.map(
        (x) =>
          ({
            position: { lat: x.address.lat, lng: x.address.lng },
            options: {
              icon: 'https://iti-images.s3.amazonaws.com/imgs/event-48.png',
              zIndex: 2,
            },
            data: x,
          } as IMapMarkerWithCustomData<VisitorPlace | VisitorEvent>)
      );

      this.keepPlaceMapMarker();
      this.mapMarkers = [...this.mapMarkers, ...markers];
      this.isShowingNearbyEventsOnMap = true;
    } else {
      this.keepPlaceMapMarker();
      this.isShowingNearbyEventsOnMap = false;
    }
  }

  public imgLoaded($event: Event) {
    this.isLoadingImg = false;
  }

  public nextImg() {
    if (!this.hasNextImg) return;

    const images = this.getPlaceImages();

    if (this.imgIndex < images.length - 1) {
      this.imgIndex++;
      this.isLoadingImg = true;
      this.currentImg = images[this.imgIndex];
      this.hasPrevImg = true;
      if (this.imgIndex === images.length - 1) {
        this.hasNextImg = false;
      }
    }
  }

  public prevImg() {
    if (!this.hasPrevImg) return;

    const images = this.getPlaceImages();

    if (this.imgIndex > 0) {
      this.imgIndex--;
      this.isLoadingImg = true;
      this.currentImg = images[this.imgIndex];
      this.hasNextImg = true;
      if (this.imgIndex === 0) {
        this.hasPrevImg = false;
      }
    }
  }

  private getPlaceImages() {
    if (this.place.isManuallyCreated) {
      if (isEmptyArray(this.place.allowedGooglePhotos)) {
        console.log('user allowed photos are empty');
        this.hasMultiImages = false;
        return [];
      }
    } else {
      if (
        isEmptyArray(this.place.allowedGooglePhotos) &&
        !isEmptyArray(this.place.blockedGooglePhotos)
      ) {
        console.log('user blocked all google images');
        this.hasMultiImages = false;
        return [];
      }
    }

    let images = clone(
      !isEmptyArray(this.place.allowedGooglePhotos)
        ? this.place.allowedGooglePhotos
        : this.place.photos.map((x) => this.getGoogleImgUrl(x.photo_reference || x.toString()))
    );

    // in DXP edit.place.images.modal, the first allowed image will be used as cover (if the original cover is not uploaded by user)
    // this in way the cover image is the same as the first allowed image, so need to remove the duplicated
    if (this.place.cover.source === images[0]) {
      images.shift();
    }

    images = [this.place.cover.source, ...images];
    images = images.slice(0, this.settings?.customSettings?.placeImagesShownCount || 10);
    if (images.length === 1) this.hasMultiImages = false;

    return images;
  }

  private copyToClip(content) {
    let aux = document.createElement('input');
    aux.setAttribute('value', content);
    document.body.appendChild(aux);
    aux.select();
    document.execCommand('copy');
    document.body.removeChild(aux);
  }

  private processPlace() {
    this.setMap();
    this.setCurrentImg();
    this.setNameIdAndSeo();
    if (this.place?.youtubeLink?.includes('watch?v=')) {
      const code = this.place.youtubeLink.split('watch?v=')[1].split('&')[0];
      this.youtubeEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://www.youtube.com/embed/${code}?autoplay=1`
      );
    } else {
      this.youtubeEmbedUrl = null;
    }
  }

  private getPlaceDetails() {
    // place.id is the google place id; AI-searched places store the google id in _id with no id field
    if (!this.place.id) {
      this.processPlace();
      this.getPlaceImages();
      this.isLoading = false;
      this.loadNearbyEvents(2);
      return;
    }

    this.placeService
      .getGooglePlaceDetails(this.hubName, this.place.id, this.place._id, false)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((place: VisitorPlace) => {
        // sometimes a Google place might be removed from Google, thus when we getting place details, it may respond "Invalid 'placeid' parameter"
        if (place.isNotExistInSource) {
          this.errorAlert.showErrorAlert(
            "Uh-oh! Looks like we haven't found this place! Please view other places in the list!"
          );
          this.dialogRef.close();
        }
        place && Object.assign(this.place, place);
        this.ensurePlaceDefaults(this.place);

        this.checkDoesHaveServes();

        this.processPlace();

        this.getPlaceImages();
      });

    const range = 2;
    this.loadNearbyEvents(range);
  }

  private setNameIdAndSeo() {
    // don't change URL for nearby events, otherwise there will be problems with the new URL
    if (this.data.from === 'nearbyOnEvent') return;

    if (!this.isScriptSolution) {
      // this.seoService.addNameIdToUrlForNonScriptSolution(
      //   this.place,
      //   this.hubName,
      //   this.dialogRef
      // );

      // SanteeCooperSC and Savannah are test accounts for SSR solution
      if (this.hubName !== 'Savannah' && this.hubName !== 'SanteeCooperSC') {
        // temporarily add these 2 for take a screenshot to show that Google can read these rich results
        this.seoService.createAndInjectGoogleStructureDataScriptForPlace(this.place);
        this.seoService.addMetaForSEO(this.place.title, this.place.description, 'Local Place');
      }

      // for client who uses place details on their page, place details view will not be a modal, but a plain html
      // in this case this.dialogRef is null, and we don't need to change the URL
      this.dialogRef && this.seoService.addNameIdToUrlForScriptSolution(this.place, this.dialogRef);
    } else {
      // for clients who use the script solution (we embed ImGoing as script into client's website)
      // change the URL by adding the ImGoing event name + Id to the params so that each place has a unique URL
      // when close the details modal, reset the URL to the original URL of the host site

      // for client who uses place details on their page, place details view will not be a modal, but a plain html
      // in this case this.dialogRef is null, and we don't need to change the URL
      this.dialogRef && this.seoService.addNameIdToUrlForScriptSolution(this.place, this.dialogRef);

      // SanteeCooperSC and Savannah are test accounts for SSR solution
      if (this.hubName !== 'Savannah' && this.hubName !== 'SanteeCooperSC') {
        this.seoService.createAndInjectGoogleStructureDataScriptForPlace(this.place);
        this.seoService.addMetaForSEO(this.place.title, this.place.description, 'Local Place');
      }
    }
  }

  keepPlaceMapMarker() {
    this.mapMarkers = this.mapMarkers.filter((x) => x.data._id === this.place._id);
  }

  isHasDine(): boolean {
    if (!this.place) return false;
    const dineOptions = (this.place as any).dine;
    if (!dineOptions) return false;

    return Object.values(dineOptions).some((option) => option === true);
  }

  isHasAccessibility(): boolean {
    if (!this.place) return false;
    const accessibilityOptions = (this.place as any).accessibilityOptions;
    if (!accessibilityOptions) return false;

    return Object.values(accessibilityOptions).some((option) => option === true);
  }

  isHasPaymentOptions(): boolean {
    if (!this.place) return false;
    const paymentOptions = (this.place as any).paymentOptions;
    if (!paymentOptions) return false;

    return Object.values(paymentOptions).some((option) => option === true);
  }

  isHasParkingOptions(): boolean {
    if (!this.place) return false;
    const parkingOptions = (this.place as any).parkingOptions;
    if (!parkingOptions) return false;

    return Object.values(parkingOptions).some((option) => option === true);
  }

  private setCurrentImg() {
    this.currentImg = this.place.cover?.source;
  }

  private getGoogleImgUrl(photoReference: string): string {
    if (photoReference.startsWith('https://')) return photoReference;

    return this.placeService.getGoogleImgUrl(
      photoReference,
      this.place.appKey || this.frontendSettings.customSettings.dataSource.googleKey
    );
    // return ConfigService.config.google.placePhoto
    //   .replace('${photo_reference}', photoReference)
    //   .replace(
    //     '${appKey}',
    //     this.place.appKey ||
    //       this.frontendSettings.customSettings.dataSource.googleKey
    //   );
  }

  private checkDoesHaveServes() {
    for (const serve in this.place?.dine) {
      if (Object.prototype.hasOwnProperty.call(this.place?.dine, serve)) {
        const element = this.place?.dine[serve];
        // currently not rendering curbside_pickup, but it is in the database
        if (element !== null && element !== undefined && serve !== 'curbside_pickup') {
          this.hasServes = true;
          break;
        }
      }
    }
  }

  private setMap() {
    this.mapMarkers = [
      {
        position: {
          lat: this.place?.lat,
          lng: this.place?.lng,
        },
        data: this.place,
      },
    ] as IMapMarkerWithCustomData<VisitorPlace | VisitorEvent>[];
  }

  private ensurePlaceDefaults(place: VisitorPlace) {
    if (!place) {
      return;
    }

    if (!place.hubName) {
      place.hubName = this.hubName;
    }

    const rawAddress: any = place.address;
    const latValue = rawAddress?.lat ?? place.lat;
    const lngValue = rawAddress?.lng ?? place.lng;
    const lat = latValue != null ? Number(latValue) : null;
    const lng = lngValue != null ? Number(lngValue) : null;
    const addressComponents = rawAddress?.addressComponents || place.address_components || [];

    if (!rawAddress || typeof rawAddress === 'string') {
      place.address = {
        address: typeof rawAddress === 'string' ? rawAddress : place.address?.address || '',
        lat,
        lng,
        addressComponents,
      };
    } else {
      if (lat != null) {
        place.address.lat = Number(place.address.lat ?? lat);
      }
      if (lng != null) {
        place.address.lng = Number(place.address.lng ?? lng);
      }
      place.address.addressComponents = addressComponents;
    }
  }
}
